import { useLayoutEffect, useRef } from 'react'

const REDUCE_MOTION = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)'

// ─── The handoff ─────────────────────────────────────────────────────────────
// When the stage changes hands, the new hero's name lifts out of their
// leaderboard row and flies into the stage (one shared-element flight,
// transform + opacity only), while the stage's supporting lines fade up.
export function useHandoff(
  swapKey: string,
  athleteId: string | null,
  rootRef: React.RefObject<HTMLDivElement>,
  nameRef: React.RefObject<HTMLDivElement>,
) {
  const prevKey = useRef<string | null>(null)
  useLayoutEffect(() => {
    const first = prevKey.current === null
    const changed = prevKey.current !== swapKey
    prevKey.current = swapKey
    if (first || !changed || !athleteId || REDUCE_MOTION) return
    const root = rootRef.current
    const name = nameRef.current
    if (!root || !name || typeof name.animate !== 'function') return
    const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(athleteId) : athleteId
    const src = root.querySelector<HTMLElement>(`[data-chorus-name="${esc}"]`)
    if (!src) return
    const r = root.getBoundingClientRect()
    const s = src.getBoundingClientRect()
    const d = name.getBoundingClientRect()
    if (!s.height || !d.height) return

    const ghost = document.createElement('div')
    ghost.className = 'sp-ghost'
    ghost.textContent = name.textContent
    ghost.style.left = `${d.left - r.left}px`
    ghost.style.top = `${d.top - r.top}px`
    root.appendChild(ghost)
    name.style.opacity = '0'
    const scale = s.height / d.height
    const flight = ghost.animate(
      [
        { transform: `translate(${s.left - d.left}px, ${s.top - d.top}px) scale(${scale})`, opacity: 0.7 },
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      ],
      { duration: 560, easing: EASE_OUT },
    )
    const done = () => {
      name.style.opacity = ''
      ghost.remove()
    }
    flight.onfinish = done
    flight.oncancel = done
    return () => {
      flight.cancel()
    }
  }, [swapKey, athleteId, rootRef, nameRef])
}
